import { CommonModule } from '@angular/common';
import { Component, computed, EventEmitter, inject, Input, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DialogComponent } from '@/shared/ui-components/dialog/dialog.component';
import { ButtonComponent } from '@/shared/ui-components/button/button.component';
import { InputComponent } from '@/shared/ui-components/input/input.component';
import { SelectComponent } from '@/shared/ui-components/select/select.component';
import { FileUploaderComponent } from '@/shared/ui-components/file-uploader/file-uploader.component';
import { KeyedOption } from '@/shared/enums/generic.enum';
import { LanguageService } from '@/shared/services/language.service';
import { buildFeedbackContext } from '../../data-access/feedback-context.util';
import { FeedbackCategory } from '../../data-access/feedback.enum';
import { feedbackCategoryOptions } from '../../data-access/feedback.option';
import { FeedbackService } from '../../feedback.service';

const MAX_DESCRIPTION_LENGTH = 4000;

/**
 * Submission dialog shared by the "My Feedback" page and the global floating widget.
 *
 * The form is guarded on `visible` rather than reset on close, matching `AskQuestionDialogComponent`:
 * projected through `app-dialog`'s `ng-content`, it would otherwise carry its draft into the next open.
 */
@Component({
    selector: 'app-feedback-dialog',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, TranslateModule, DialogComponent, ButtonComponent, InputComponent, SelectComponent, FileUploaderComponent],
    templateUrl: './feedback-dialog.component.html'
})
export class FeedbackDialogComponent {
    @Input() public visible = false;
    /** Read-only "Feedback about: {{ contextLabel }}" chip. Set by the floating widget only. */
    @Input() public contextLabel: string | null = null;

    @Output() public visibleChange = new EventEmitter<boolean>();
    @Output() public submitted = new EventEmitter<void>();

    public readonly form: FormGroup;

    public readonly _feedbackService = inject(FeedbackService);

    private readonly _formBuilder = inject(FormBuilder);
    private readonly _router = inject(Router);
    private readonly _activatedRoute = inject(ActivatedRoute);
    private readonly _translateService = inject(TranslateService);
    private readonly _languageService = inject(LanguageService);

    public readonly categoryOptions = computed(() => this.translateOptions(feedbackCategoryOptions));

    constructor() {
        this.form = this._formBuilder.group({
            category: [FeedbackCategory.GENERAL, Validators.required],
            description: ['', [Validators.required, Validators.maxLength(MAX_DESCRIPTION_LENGTH)]],
            attachment: [[] as File[]]
        });
    }

    public onVisibleChange(value: boolean): void {
        this.visibleChange.emit(value);
    }

    public close(): void {
        this.reset();
        this.visibleChange.emit(false);
    }

    public submit(): void {
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }

        const raw = this.form.getRawValue() as { category: FeedbackCategory; description: string; attachment: File[] };
        const context = buildFeedbackContext(this._router, this._activatedRoute);

        this._feedbackService.submitFeedback(
            {
                ...context,
                category: raw.category,
                description: raw.description.trim(),
                attachment: raw.attachment?.[0] ?? null
            },
            () => {
                this.reset();
                this.submitted.emit();
            }
        );
    }

    private reset(): void {
        this.form.reset({ category: FeedbackCategory.GENERAL, description: '', attachment: [] });
    }

    private translateOptions<T>(options: KeyedOption<T>[]): Array<{ label: string; value: T }> {
        if (!this._languageService.ready()) return [];
        return options.map((option) => ({
            label: this._translateService.instant(option.labelKey) as string,
            value: option.value
        }));
    }
}
